/**
 * Pure helpers behind the Steward card. Kept out of the component so the
 * load-bearing contract — how a proposed card becomes a real itinerary row —
 * is unit-testable without a DOM.
 */
import type {
  ItineraryItemInput,
  StewardCardData,
  StewardCategory,
  StewardPrefill,
  StewardScope,
} from "@/lib/types";

/**
 * The eyebrow above a card's title. Mirrors the backend's CATEGORY_LABEL — the
 * traveler asked for a rental car, so the card says "Rental car", not the
 * "Transfer" it will be stored as.
 */
export const CATEGORY_LABEL: Record<StewardCategory, string> = {
  hotel: "Stay",
  rental: "Stay",
  dining: "Eat",
  activity: "Do",
  attraction: "Do",
  tour: "Tour",
  car: "Rental car",
  train: "Train",
  transfer: "Transfer",
  event: "Event",
};

/**
 * Categories we actually go and price. A missing number is only notable for
 * these — for a tour or a table we never promised one, so an "unavailable" note
 * there would apologise for a promise nobody made. Cars joined this set when
 * live car pricing shipped; before that, a car card silently having no price
 * was the correct and expected state.
 */
const PRICEABLE_CATEGORIES = new Set<StewardCategory>(["hotel", "rental", "car"]);

/**
 * Map a card's prefill onto the body of the EXISTING
 * POST /trips/:id/itinerary-items. This is the whole add-to-itinerary contract:
 * the model never writes an item, it only proposes this body, and the user's tap
 * sends it through the same API the manual add dialog uses.
 */
export function prefillToItemInput(prefill: StewardPrefill): ItineraryItemInput {
  return {
    item_type: prefill.item_type,
    title: prefill.title,
    city: prefill.city ?? null,
    location: prefill.location ?? null,
    day_date: prefill.day_date,
    start_time: prefill.start_time ?? null,
    end_time: prefill.end_time ?? null,
    notes: prefill.notes ?? null,
  };
}

/** "4.5 (1,200)" — never rendered unless the score carried a source. */
export function formatRating(card: StewardCardData): string | null {
  if (!card.rating) return null;
  const score = card.rating.score.toFixed(1).replace(/\.0$/, "");
  return card.rating.count != null
    ? `${score} (${card.rating.count.toLocaleString()})`
    : score;
}

/**
 * "night" reads like a human; "per_night" reads like a database column.
 *
 * ⚠️ "total" MUST NOT become "/total". A rental car is quoted as a whole-hire
 * total, and the old generic `/${period}` branch would have rendered
 * "$226/total" — which reads as a rate and is exactly the misreading that turns
 * a correct number into a wrong one. It gets a leading space and no slash.
 */
function periodLabel(period: string | undefined): string {
  if (!period) return "";
  if (period === "per_night" || period === "night") return "/night";
  if (period === "total") return " total";
  return `/${period}`;
}

/** "$240/night" or "$$$" — never rendered unless the price carried a source. */
export function formatPrice(card: StewardCardData): string | null {
  const p = card.price;
  if (!p) return null;
  if (p.amount != null) {
    const symbol = p.currency === "USD" ? "$" : `${p.currency} `;
    return `${symbol}${Math.round(p.amount).toLocaleString()}${periodLabel(p.period)}`;
  }
  return p.range ?? null;
}

/**
 * "Sep 11–13" — the nights a rate was quoted for, or null when the server did
 * not attach them.
 *
 * A rate is only a fact WITH its dates, so the card renders this beside every
 * nightly number. Same-month stays collapse to one month name; a stay that
 * crosses a month keeps both ("Sep 30 – Oct 2").
 */
export function formatRateDates(card: StewardCardData): string | null {
  const p = card.price;
  if (!p?.checkin || !p?.checkout) return null;
  const parse = (ymd: string) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return y && m && d ? { y, m, d } : null;
  };
  const a = parse(p.checkin);
  const b = parse(p.checkout);
  if (!a || !b) return null;
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const left = `${MONTHS[a.m - 1]} ${a.d}`;
  const right = a.m === b.m && a.y === b.y ? `${b.d}` : `${MONTHS[b.m - 1]} ${b.d}`;
  return `${left}–${right}`;
}

/** "Google Hotels" / "captaincook.com" — where a number came from, in one chip. */
export function priceSourceLabel(card: StewardCardData): string | null {
  const src = card.price?.source;
  if (!src) return null;
  try {
    const host = new URL(src).hostname.replace(/^www\./, "");
    if (host === "google.com" && /\/travel\//.test(new URL(src).pathname)) return "Google Hotels";
    return host;
  } catch {
    return null;
  }
}

/**
 * Should this card say "rates unavailable" out loud?
 *
 * TRUE only for a STAY we recommended but could not price. This is deliberately
 * an explicit state and not an empty space: a blank where a nightly rate belongs
 * reads as "free", or as an oversight, and the traveler's next move only happens
 * if we tell them to make it.
 *
 * It is deliberately FALSE for a car, a tour, a train or a table. We never claim
 * to price those, so "rates unavailable" there would apologise for a promise
 * nobody made — those cards carry a search link instead, which is the useful
 * thing to show.
 */
export function ratesUnavailable(card: StewardCardData): boolean {
  return PRICEABLE_CATEGORIES.has(card.type) && card.price?.amount == null;
}

/** "Sep 11 · Anchorage · a stay" — the add-slot context, for the sheet header. */
export function scopeHeadline(scope: StewardScope | null | undefined): string | null {
  if (!scope) return null;
  const bits = [
    scope.date ? formatCardDay(scope.date) : null,
    scope.city?.trim() || null,
    scope.category ? CATEGORY_LABEL[scope.category].toLowerCase() : null,
  ].filter(Boolean);
  return bits.length > 0 ? bits.join(" · ") : null;
}

/** "Thu Sep 10" — the day this card would land on. */
export function formatCardDay(dayDate: string): string {
  const [y, m, d] = dayDate.split("-").map(Number);
  if (!y || !m || !d) return dayDate;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** The one-line subtitle under the title: area, city, and when. */
export function cardSubtitle(card: StewardCardData): string {
  const where = card.area ? `${card.area}, ${card.city}` : card.city;
  return `${where} · ${formatCardDay(card.itinerary_prefill.day_date)}`;
}
